import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { loadStripe, type Stripe as StripeType } from '@stripe/stripe-js';
import {
  Elements,
  PaymentElement,
  useElements,
  useStripe,
} from '@stripe/react-stripe-js';
import { Lock, CheckCircle2, Sparkles } from 'lucide-react';
import { Button } from '@/client/components/ui/button';
import { Alert } from '@/client/components/ui/alert';
import { api, ApiError } from '@/client/lib/api';
import { cn } from '@/client/lib/utils';
import { isLocale, type Locale } from '@/client/i18n';

type ConfigResponse = { stripePublishableKey: string | null; environment: string };

type BookingPaymentInfo = {
  consultation: {
    id: string;
    title: string;
    durationMinutes: number;
    priceCents: number;
    currency: string;
    format: 'online' | 'in_person' | 'hybrid';
  };
  slot: { startAt: number; endAt: number };
  contact: { name: string | null; email: string | null };
  expiresAt: number | null;
};

type CheckoutResponse =
  | {
      clientSecret: string;
      amount: number;
      breakdown: {
        basePriceCents: number;
        discountCents: number;
        totalCents: number;
        taxCents: number;
      };
    }
  | { freeAccess: true; bookingId: string };

const stripePromiseCache = new Map<string, Promise<StripeType | null>>();
function getStripePromise(publishableKey: string): Promise<StripeType | null> {
  let p = stripePromiseCache.get(publishableKey);
  if (!p) {
    p = loadStripe(publishableKey);
    stripePromiseCache.set(publishableKey, p);
  }
  return p;
}

export function BookingPayment() {
  const params = useParams();
  const [searchParams] = useSearchParams();
  const locale: Locale = isLocale(params.locale) ? params.locale : 'sk';
  const token = searchParams.get('token') ?? '';

  const config = useQuery({
    queryKey: ['config'],
    queryFn: () => api<ConfigResponse>('/api/v1/config'),
  });
  const info = useQuery({
    queryKey: ['booking-payment-info', token],
    queryFn: () =>
      api<BookingPaymentInfo>(`/api/v1/booking-payments/info?paymentToken=${encodeURIComponent(token)}`),
    enabled: token.length > 0,
    retry: false,
  });

  useEffect(() => {
    document.title = `${locale === 'cs' ? 'Platba konzultace' : 'Platba konzultácie'} | Akadémia Dajany Rodriguez`;
  }, [locale]);

  if (!token) {
    return <ErrorPage locale={locale} message={locale === 'cs' ? 'Chybí platební odkaz.' : 'Chýba platobný odkaz.'} />;
  }
  if (config.isLoading || info.isLoading) {
    return (
      <div className="px-6 py-32 text-center font-sans text-body text-text-secondary">
        <div className="inline-flex items-center gap-3">
          <span className="h-2 w-2 rounded-full bg-primary animate-pulse" />
          {locale === 'cs' ? 'Načítáme…' : 'Načítavame…'}
        </div>
      </div>
    );
  }
  if (info.error) {
    if (info.error instanceof ApiError && info.error.status === 409) {
      return (
        <ErrorPage
          locale={locale}
          message={
            locale === 'cs'
              ? 'Tento platební odkaz již nelze použít. Buď byl už zaplacen, nebo vypršel.'
              : 'Tento platobný odkaz sa už nedá použiť. Buď bol už zaplatený, alebo vypršal.'
          }
        />
      );
    }
    return (
      <ErrorPage
        locale={locale}
        message={
          locale === 'cs'
            ? 'Platební odkaz nelze najít. Zkontroluj e-mail.'
            : 'Platobný odkaz sa nepodarilo nájsť. Skontroluj e-mail.'
        }
      />
    );
  }
  if (!info.data || !config.data?.stripePublishableKey) {
    return (
      <ErrorPage
        locale={locale}
        message={locale === 'cs' ? 'Neočekávaná chyba.' : 'Neočakávaná chyba.'}
      />
    );
  }

  return (
    <BookingPaymentInner
      locale={locale}
      info={info.data}
      paymentToken={token}
      stripePromise={getStripePromise(config.data.stripePublishableKey)}
    />
  );
}

function ErrorPage({ locale, message }: { locale: Locale; message: string }) {
  return (
    <div className="relative">
      <section className="relative mx-auto max-w-xl px-5 sm:px-6 lg:px-8 py-16 sm:py-24">
        <div className="dr-card-strong p-10 sm:p-14 text-center animate-fade-up">
          <p className="dr-eyebrow">
            {locale === 'cs' ? 'Chyba' : 'Chyba'}
          </p>
          <h1 className="mt-3 font-display text-h2 text-primary-dark">
            {locale === 'cs' ? 'Něco se pokazilo' : 'Niečo sa pokazilo'}
          </h1>
          <p className="mt-4 font-display text-body-large text-text-secondary leading-relaxed">
            {message}
          </p>
        </div>
      </section>
    </div>
  );
}

function BookingPaymentInner({
  locale,
  info,
  paymentToken,
  stripePromise,
}: {
  locale: Locale;
  info: BookingPaymentInfo;
  paymentToken: string;
  stripePromise: Promise<StripeType | null>;
}) {
  const elementsOptions = useMemo(
    () => ({
      mode: 'payment' as const,
      amount: Math.max(info.consultation.priceCents, 50),
      currency: info.consultation.currency.toLowerCase(),
      paymentMethodCreation: 'manual' as const,
      // Pin to ['card'] in lockstep with booking-payments.ts payment_settings.
      paymentMethodTypes: ['card'],
      locale: locale === 'cs' ? ('cs' as const) : ('sk' as const),
      appearance: {
        theme: 'stripe' as const,
        variables: {
          colorPrimary: '#753BBD',
          colorBackground: '#ffffff',
          colorText: '#1a1025',
          colorTextSecondary: '#6b5a7a',
          colorDanger: '#c4322d',
          fontFamily: '"DM Sans", system-ui, sans-serif',
          borderRadius: '14px',
          spacingUnit: '4px',
        },
        rules: {
          '.Input': {
            border: '1px solid rgba(117, 59, 189, 0.18)',
            boxShadow: 'none',
            backgroundColor: 'rgba(255, 255, 255, 0.85)',
          },
          '.Input:focus': {
            border: '1px solid #753BBD',
            boxShadow: '0 0 0 3px rgba(117, 59, 189, 0.15)',
          },
        },
      },
    }),
    [info.consultation.priceCents, info.consultation.currency, locale],
  );

  const fmt = (cents: number) =>
    new Intl.NumberFormat(locale === 'cs' ? 'cs-CZ' : 'sk-SK', {
      style: 'currency',
      currency: info.consultation.currency,
      minimumFractionDigits: 2,
    }).format(cents / 100);

  const slotDate = new Date(info.slot.startAt * 1000);
  const slotLabel = slotDate.toLocaleString(locale === 'cs' ? 'cs-CZ' : 'sk-SK', {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
    hour: '2-digit',
    minute: '2-digit',
  });

  return (
    <article className="relative">
      <div
        className="absolute top-10 -left-32 w-[26rem] h-[26rem] rounded-full pointer-events-none animate-float-orb"
        style={{
          background: 'radial-gradient(circle, rgba(193,167,226,0.40) 0%, transparent 65%)',
          filter: 'blur(40px)',
        }}
        aria-hidden
      />
      <div
        className="absolute top-72 -right-32 w-[28rem] h-[28rem] rounded-full pointer-events-none animate-float-orb"
        style={{
          background: 'radial-gradient(circle, rgba(117,59,189,0.18) 0%, transparent 65%)',
          filter: 'blur(48px)',
          animationDelay: '-12s',
        }}
        aria-hidden
      />

      <div className="relative mx-auto max-w-6xl px-5 sm:px-6 lg:px-8 py-10 sm:py-14 lg:py-16">
        <div className="mb-10 max-w-2xl">
          <p className="dr-eyebrow animate-fade-up">
            {locale === 'cs' ? 'Bezpečná platba' : 'Bezpečná platba'}
          </p>
          <h1
            className="mt-3 font-display text-[clamp(2rem,5vw,3.5rem)] leading-[1.05] tracking-tight text-primary-dark animate-fade-up"
            style={{ animationDelay: '70ms' }}
          >
            {locale === 'cs' ? 'Platba konzultace' : 'Platba konzultácie'}
          </h1>
        </div>

        <div className="grid gap-8 lg:gap-10 lg:grid-cols-[1fr_22rem]">
          {/* Main column */}
          <div className="min-w-0">
            <Elements stripe={stripePromise} options={elementsOptions}>
              <BookingCheckoutForm
                locale={locale}
                info={info}
                paymentToken={paymentToken}
              />
            </Elements>
          </div>

          {/* Sticky summary */}
          <aside className="lg:sticky lg:top-24 self-start space-y-5 animate-fade-up" style={{ animationDelay: '140ms' }}>
            <section className="dr-card-static p-6">
              <h2 className="font-display text-h4 text-primary-dark">
                {locale === 'cs' ? 'Souhrn objednávky' : 'Súhrn objednávky'}
              </h2>

              <div className="mt-4 rounded-2xl bg-gradient-to-br from-primary/10 to-primary/5 border border-primary/15 p-4">
                <p className="font-display text-body-large text-primary-dark leading-tight">
                  {info.consultation.title}
                </p>
                <p className="mt-1.5 font-sans text-body-small text-text-secondary">
                  {info.consultation.durationMinutes} min
                </p>
                <p className="mt-1 font-sans text-body-small text-text-secondary first-letter:uppercase">
                  {slotLabel}
                </p>
              </div>

              <dl className="mt-5 space-y-2 font-sans text-body-small">
                <div className="flex justify-between border-t border-glass-border-strong pt-3">
                  <dt className="font-display text-h4 text-primary-dark">
                    {locale === 'cs' ? 'Celkem' : 'Spolu'}
                  </dt>
                  <dd className="font-display text-h4 text-primary-dark">
                    {fmt(info.consultation.priceCents)}
                  </dd>
                </div>
                <p className="font-sans text-caption text-text-muted">
                  {locale === 'cs'
                    ? 'Cena je včetně DPH (uplatní se Stripe Tax podle fakturační adresy).'
                    : 'Cena je vrátane DPH (uplatní sa Stripe Tax podľa fakturačnej adresy).'}
                </p>
              </dl>
            </section>

            <div className="rounded-2xl p-4 bg-white/40 backdrop-blur-md border border-glass-border">
              <ul className="space-y-2 font-sans text-caption text-text-secondary">
                <li className="inline-flex items-center gap-2">
                  <Lock size={12} className="text-primary" />
                  {locale === 'cs' ? 'Šifrované Stripe spojení' : 'Šifrované Stripe spojenie'}
                </li>
                <li className="inline-flex items-center gap-2">
                  <CheckCircle2 size={12} className="text-success" />
                  {locale === 'cs' ? 'Faktura e-mailem po platbě' : 'Faktúra na e-mail po platbe'}
                </li>
                <li className="inline-flex items-center gap-2">
                  <Sparkles size={12} className="text-primary" />
                  {locale === 'cs' ? 'Po platbě obdržíš pozvánku' : 'Po platbe dostaneš pozvánku'}
                </li>
              </ul>
            </div>
          </aside>
        </div>
      </div>
    </article>
  );
}

type BillingFormState = {
  type: 'individual' | 'company';
  name: string;
  email: string;
  phone: string;
  companyName: string;
  ico: string;
  dic: string;
  icDph: string;
  street: string;
  city: string;
  zip: string;
  country: string;
};

function BookingCheckoutForm({
  locale,
  info,
  paymentToken,
}: {
  locale: Locale;
  info: BookingPaymentInfo;
  paymentToken: string;
}) {
  const stripe = useStripe();
  const elements = useElements();
  const navigate = useNavigate();

  const [billing, setBilling] = useState<BillingFormState>({
    type: 'individual',
    name: info.contact.name ?? '',
    email: info.contact.email ?? '',
    phone: '',
    companyName: '',
    ico: '',
    dic: '',
    icDph: '',
    street: '',
    city: '',
    zip: '',
    country: 'SK',
  });
  const [consent, setConsent] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const idempotencyKeyRef = useRef<string>(crypto.randomUUID());

  function patch<K extends keyof BillingFormState>(key: K, value: BillingFormState[K]) {
    setBilling((b) => ({ ...b, [key]: value }));
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    if (!consent) return;
    setSubmitting(true);
    try {
      if (!stripe || !elements) {
        setError(locale === 'cs' ? 'Načítaní platby selhalo.' : 'Načítanie platby zlyhalo.');
        return;
      }
      const submitResult = await elements.submit();
      if (submitResult.error) {
        setError(submitResult.error.message ?? null);
        return;
      }

      const body = {
        paymentToken,
        consultationTermsAccepted: true as const,
        billingData:
          billing.type === 'individual'
            ? {
                type: 'individual' as const,
                name: billing.name,
                email: billing.email,
                phone: billing.phone || undefined,
                address: {
                  street: billing.street,
                  city: billing.city,
                  zip: billing.zip,
                  country: billing.country,
                },
              }
            : {
                type: 'company' as const,
                name: billing.name,
                email: billing.email,
                phone: billing.phone || undefined,
                companyName: billing.companyName,
                ico: billing.ico || undefined,
                dic: billing.dic || undefined,
                icDph: billing.icDph || undefined,
                address: {
                  street: billing.street,
                  city: billing.city,
                  zip: billing.zip,
                  country: billing.country,
                },
              },
      };

      const response = await fetch('/api/v1/booking-payments/checkout', {
        method: 'POST',
        credentials: 'same-origin',
        headers: {
          'Content-Type': 'application/json',
          'Idempotency-Key': idempotencyKeyRef.current,
        },
        body: JSON.stringify(body),
      });
      if (!response.ok) {
        if (response.status === 409) {
          setError(
            locale === 'cs'
              ? 'Odkaz již vypršel nebo byl použit.'
              : 'Odkaz už vypršal alebo bol použitý.',
          );
          return;
        }
        setError(
          locale === 'cs' ? 'Něco se pokazilo. Zkus znovu.' : 'Niečo sa pokazilo. Skús znova.',
        );
        return;
      }
      const json = (await response.json()) as { data: CheckoutResponse };
      const data = json.data;

      if ('freeAccess' in data && data.freeAccess) {
        navigate(`/${locale}/portal/rezervacie?welcome=1`, { replace: true });
        return;
      }

      const paid = data as Extract<CheckoutResponse, { clientSecret: string }>;
      const result = await stripe.confirmPayment({
        elements,
        clientSecret: paid.clientSecret,
        confirmParams: {
          return_url: `${window.location.origin}/${locale}/portal/rezervacie?welcome=1`,
        },
        redirect: 'if_required',
      });
      if (result.error) {
        setError(
          result.error.message ?? (locale === 'cs' ? 'Platba selhala.' : 'Platba zlyhala.'),
        );
        return;
      }
      navigate(`/${locale}/portal/rezervacie?welcome=1`, { replace: true });
    } catch {
      setError(locale === 'cs' ? 'Něco se pokazilo.' : 'Niečo sa pokazilo.');
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <form className="space-y-6 animate-fade-up" onSubmit={handleSubmit} noValidate>
      <section className="dr-card-static p-6 sm:p-8">
        <h2 className="font-display text-h3 text-primary-dark">
          {locale === 'cs' ? 'Fakturační údaje' : 'Fakturačné údaje'}
        </h2>
        <p className="mt-1 font-sans text-body-small text-text-secondary">
          {locale === 'cs'
            ? 'Tyto údaje se použijí na fakturu.'
            : 'Tieto údaje sa použijú na faktúru.'}
        </p>

        <div className="mt-5 flex flex-wrap gap-2 p-1.5 rounded-pill bg-white/60 backdrop-blur-md border border-glass-border-strong w-fit">
          {(['individual', 'company'] as const).map((type) => (
            <button
              key={type}
              type="button"
              onClick={() => patch('type', type)}
              className={cn(
                'rounded-pill px-4 py-1.5 font-sans text-body-small font-medium transition-all',
                billing.type === type
                  ? 'bg-gradient-to-br from-primary to-primary-dark text-white shadow-[0_2px_8px_rgba(117,59,189,0.25)]'
                  : 'text-text-secondary hover:text-primary',
              )}
            >
              {type === 'individual'
                ? locale === 'cs'
                  ? 'Fyzická osoba'
                  : 'Fyzická osoba'
                : locale === 'cs'
                  ? 'Firma'
                  : 'Firma'}
            </button>
          ))}
        </div>

        <div className="mt-6 grid gap-4 md:grid-cols-2">
          <Field label={locale === 'cs' ? 'Jméno' : 'Meno'} value={billing.name} onChange={(v) => patch('name', v)} required />
          <Field label="E-mail" value={billing.email} onChange={(v) => patch('email', v)} type="email" required />
          <Field label={locale === 'cs' ? 'Telefon' : 'Telefón'} value={billing.phone} onChange={(v) => patch('phone', v)} type="tel" />
          {billing.type === 'company' && (
            <>
              <Field label={locale === 'cs' ? 'Firma' : 'Firma'} value={billing.companyName} onChange={(v) => patch('companyName', v)} required />
              <Field label="IČO" value={billing.ico} onChange={(v) => patch('ico', v)} />
              <Field label="DIČ" value={billing.dic} onChange={(v) => patch('dic', v)} />
              <Field label="IČ DPH" value={billing.icDph} onChange={(v) => patch('icDph', v)} placeholder="SK1234567890" />
            </>
          )}
          <Field label={locale === 'cs' ? 'Ulice' : 'Ulica'} value={billing.street} onChange={(v) => patch('street', v)} required className="md:col-span-2" />
          <Field label={locale === 'cs' ? 'Město' : 'Mesto'} value={billing.city} onChange={(v) => patch('city', v)} required />
          <Field label="PSČ" value={billing.zip} onChange={(v) => patch('zip', v)} required />
          <Field label={locale === 'cs' ? 'Země (ISO)' : 'Krajina (ISO)'} value={billing.country} onChange={(v) => patch('country', v.toUpperCase().slice(0, 2))} required />
        </div>
      </section>

      <section className="dr-card-static p-6 sm:p-8">
        <h2 className="font-display text-h3 text-primary-dark inline-flex items-center gap-2">
          <Lock size={18} className="text-primary" />
          {locale === 'cs' ? 'Platba' : 'Platba'}
        </h2>
        <p className="mt-1 font-sans text-body-small text-text-secondary">
          {locale === 'cs'
            ? 'Tvoja platba je zpracovaná přes Stripe.'
            : 'Tvoja platba je spracovaná cez Stripe.'}
        </p>
        <div className="mt-5">
          <PaymentElement options={{ layout: 'tabs' }} />
        </div>
      </section>

      <label
        className={cn(
          'flex items-start gap-3 rounded-2xl p-4 cursor-pointer transition-all',
          consent
            ? 'bg-primary-50/80 border border-primary/25'
            : 'bg-white/70 border border-glass-border-strong hover:border-primary/25',
        )}
      >
        <input
          type="checkbox"
          checked={consent}
          onChange={(e) => setConsent(e.target.checked)}
          required
          className="mt-0.5 h-5 w-5 accent-primary cursor-pointer"
        />
        <span className="font-sans text-body-small text-text-secondary leading-relaxed">
          {locale === 'cs'
            ? 'Beru na vědomí 14denní právo odstoupit od smlouvy. Po uskutečnění konzultace v plném rozsahu právo odstoupit zaniká podle § 19 slovenského zákona č. 108/2024 Z. z. Konzultaci mohu bezplatně zrušit nejpozději 24 hodin před dohodnutým termínem.'
            : 'Beriem na vedomie 14-dňové právo odstúpiť od zmluvy. Po uskutočnení konzultácie v plnom rozsahu právo odstúpiť zaniká podľa § 19 zákona č. 108/2024 Z. z. Konzultáciu môžem bezplatne zrušiť najneskôr 24 hodín pred dohodnutým termínom.'}
        </span>
      </label>

      {error && <Alert variant="error">{error}</Alert>}

      <Button type="submit" size="lg" disabled={!consent || submitting} className="w-full">
        {submitting
          ? locale === 'cs'
            ? 'Zpracovávám…'
            : 'Spracovávam…'
          : locale === 'cs'
            ? `Zaplatit ${(info.consultation.priceCents / 100).toFixed(2)} €`
            : `Zaplatiť ${(info.consultation.priceCents / 100).toFixed(2)} €`}
      </Button>
    </form>
  );
}

function Field(props: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  type?: string;
  required?: boolean;
  placeholder?: string;
  className?: string;
}) {
  return (
    <label className={cn('flex flex-col gap-1.5', props.className)}>
      <span className="font-sans text-body-small font-medium text-text-secondary">
        {props.label}
        {props.required && <span className="text-primary ml-0.5">*</span>}
      </span>
      <input
        type={props.type ?? 'text'}
        value={props.value}
        onChange={(e) => props.onChange(e.target.value)}
        required={props.required}
        placeholder={props.placeholder}
        className="rounded-md bg-white/80 backdrop-blur-md border border-glass-border-strong px-4 h-11 font-sans text-body text-text-primary placeholder:text-text-muted hover:border-primary/30 focus-visible:outline-none focus-visible:border-primary focus-visible:shadow-glow focus-visible:bg-white transition-all"
      />
    </label>
  );
}
